/**
 * SeoRobotsGate — respects the super-admin "Search & AI crawler visibility"
 * toggle at /admin › System Ops + injects Google Search Console + Bing
 * Webmaster Tools verification tags stored in system_settings.
 *
 * When the crawler toggle is OFF, we inject `<meta name="robots"
 * content="noindex,nofollow">` into the document head so Google, Bing,
 * Apple, GPTBot, ClaudeBot, PerplexityBot, Gemini, and every other
 * well-behaved crawler will skip indexing this page. When the toggle is
 * ON (default), any previously injected tag is removed so the site
 * becomes discoverable again without a full reload.
 *
 * Site-verification tokens (google-site-verification, msvalidate.01) are
 * managed the same way: injected when set in system_settings, removed
 * when the admin clears them. This lets a super-admin add / rotate their
 * verification codes without redeploying the SPA.
 *
 * Renders nothing — pure side-effect component.
 */
import { useEffect } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const META_ROBOTS_ID = "glpflo-robots-gate";
const META_GOOGLE_ID = "glpflo-google-verify";
const META_BING_ID = "glpflo-bing-verify";
const SAMEAS_SCRIPT_ID = "glpflo-sameas-supplement";

function upsertMeta(id, name, content) {
  let el = document.getElementById(id);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.id = id;
    el.name = name;
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function SeoRobotsGate() {
  useEffect(() => {
    let cancelled = false;
    async function apply() {
      try {
        const { data } = await axios.get(`${API_BASE}/api/system/seo-status`, {
          timeout: 5000,
        });
        if (cancelled) return;
        const enabled = data?.enabled !== false; // default ON
        // Robots toggle
        const existing = document.getElementById(META_ROBOTS_ID);
        if (!enabled) {
          if (!existing) {
            const meta = document.createElement("meta");
            meta.id = META_ROBOTS_ID;
            meta.name = "robots";
            meta.content = "noindex,nofollow";
            document.head.appendChild(meta);
          }
        } else if (existing) {
          existing.remove();
        }
        // Verification tokens — injected only when set. Google uses the
        // `google-site-verification` meta; Bing uses `msvalidate.01`.
        upsertMeta(META_GOOGLE_ID, "google-site-verification", data?.google_site_verification || "");
        upsertMeta(META_BING_ID, "msvalidate.01", data?.bing_site_verification || "");
      } catch {
        // Fail-open: if the status endpoint is unreachable, don't block indexing.
      }
      // Independently: pull the sameAs URL list managed by the AI Citation
      // Tracker and inject a supplemental MedicalOrganization schema. This
      // lets admins add Trustpilot / BBB / LinkedIn / Google Business links
      // without a redeploy — the schema updates on the next SPA load.
      try {
        const { data: urls } = await axios.get(`${API_BASE}/api/ai-citations/same-as`, { timeout: 5000 });
        const existing = document.getElementById(SAMEAS_SCRIPT_ID);
        if (Array.isArray(urls) && urls.length > 0) {
          const payload = {
            "@context": "https://schema.org",
            "@type": "MedicalOrganization",
            "@id": "https://glpflo.org/#org-sameAs",
            "name": "GLPFlo",
            "url": "https://glpflo.org/",
            "sameAs": urls,
          };
          if (!existing) {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.id = SAMEAS_SCRIPT_ID;
            s.textContent = JSON.stringify(payload);
            document.head.appendChild(s);
          } else {
            existing.textContent = JSON.stringify(payload);
          }
        } else if (existing) {
          existing.remove();
        }
      } catch {
        // Non-fatal — sameAs supplement is a nice-to-have.
      }
    }
    apply();
    return () => { cancelled = true; };
  }, []);
  return null;
}
